import { useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { chatApi } from "../../api/chat";
import { chatKeys } from "../queries/useChatMessages";
import { markChatVisited } from "../useChatLastVisit";
import { getLoggedUser } from "../../util/SecurityUtils";
import type { ChatMessage, ReactionSummary } from "../../types/domain";

interface SendMessageVars {
  content: string;
  mentionedPlayerIds?: number[];
  replyToMessageId?: number | null;
}

export const useSendMessage = (tournamentId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ content, mentionedPlayerIds = [], replyToMessageId = null }: SendMessageVars) =>
      chatApi.sendMessage(content, tournamentId, mentionedPlayerIds, replyToMessageId),
    onSuccess: (msg) => {
      // WS event will refresh the cache; but for safety in case WS is down, patch locally.
      queryClient.setQueriesData<ChatPagesData>({ queryKey: chatKeys.scope(tournamentId) }, (old) => {
        if (!old?.pages || old.pages.length === 0) return old;
        if (old.pages[0].some((m) => m.id === msg.id)) return old;
        const [first, ...rest] = old.pages;
        return { ...old, pages: [[msg, ...first], ...rest] };
      });
      queryClient.invalidateQueries({ queryKey: chatKeys.latestTimestamp(tournamentId) });
      const serverTime = new Date(msg.createdAt).getTime();
      markChatVisited(Math.max(Date.now(), serverTime), tournamentId);
    },
  });
};

interface EditMessageVars {
  id: number;
  content: string;
  mentionedPlayerIds?: number[];
}

export const useEditMessage = (tournamentId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content, mentionedPlayerIds = [] }: EditMessageVars) =>
      chatApi.editMessage(id, content, mentionedPlayerIds),
    onSuccess: (msg) => {
      queryClient.setQueriesData<ChatPagesData>({ queryKey: chatKeys.scope(tournamentId) }, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => page.map((m) => (m.id === msg.id ? { ...msg, authorName: m.authorName } : m))),
        };
      });
    },
  });
};

export const useDeleteMessage = (isAdmin: boolean, tournamentId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => (isAdmin ? chatApi.deleteAsAdmin(id) : chatApi.deleteOwn(id)),
    onSuccess: (_res, id) => {
      queryClient.setQueriesData<ChatPagesData>({ queryKey: chatKeys.scope(tournamentId) }, (old) => {
        if (!old?.pages) return old;
        return { ...old, pages: old.pages.map((page) => page.filter((m) => m.id !== id)) };
      });
    },
  });
};

export const usePinMessage = (tournamentId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: number; pinned: boolean }) =>
      pinned ? chatApi.pinMessage(id) : chatApi.unpinMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.pinned(tournamentId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.scope(tournamentId) });
    },
  });
};

type ChatPagesData = InfiniteData<ChatMessage[]> | undefined;

interface ReactionMutationVars {
  messageId: number;
  emoji: string;
}

function applyReactionToggle(reactions: ReactionSummary[], emoji: string, add: boolean): ReactionSummary[] {
  const currentUser = getLoggedUser();
  const currentUserName = currentUser?.username ?? "";
  const currentUserId = currentUser?.id ?? -1;
  const existing = reactions.find(r => r.emoji === emoji);
  if (add) {
    if (existing) {
      if (existing.reactedByUser) return reactions;
      return reactions.map(r =>
        r.emoji === emoji
          ? {
              ...r,
              count: r.count + 1,
              reactedByUser: true,
              users: [...r.users, currentUserName],
              userIds: [...(r.userIds ?? []), currentUserId],
            }
          : r
      );
    }
    return [...reactions, {
      emoji,
      count: 1,
      reactedByUser: true,
      users: [currentUserName],
      userIds: [currentUserId],
    }];
  }
  if (!existing || !existing.reactedByUser) return reactions;
  if (existing.count <= 1) return reactions.filter(r => r.emoji !== emoji);
  return reactions.map(r =>
    r.emoji === emoji
      ? {
          ...r,
          count: r.count - 1,
          reactedByUser: false,
          users: r.users.filter(name => name !== currentUserName),
          userIds: (r.userIds ?? []).filter(uid => uid !== currentUserId),
        }
      : r
  );
}

function useReactionMutation(
  mutationFn: (vars: ReactionMutationVars) => Promise<unknown>,
  add: boolean,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async ({ messageId, emoji }) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.all });
      const snapshots = queryClient.getQueriesData({ queryKey: chatKeys.all });
      queryClient.setQueriesData<ChatPagesData>({ queryKey: chatKeys.all }, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.map((msg) =>
              msg.id === messageId
                ? { ...msg, reactions: applyReactionToggle(msg.reactions ?? [], emoji, add) }
                : msg
            )
          ),
        };
      });
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
  });
}

export const useAddReaction = () =>
  useReactionMutation(({ messageId, emoji }) => chatApi.addReaction(messageId, emoji), true);

export const useRemoveReaction = () =>
  useReactionMutation(({ messageId, emoji }) => chatApi.removeReaction(messageId, emoji), false);
